import React, { useEffect } from 'react';
import { ThemeProvider } from 'flowbite-react';
import { DataManagerProvider } from './DataManagerContext';
import Router from './router';
import './styles.css';
import { Authenticator } from '@aws-amplify/ui-react';
import { SnackbarProvider } from 'notistack';
import "aws-amplify/auth/enable-oauth-listener";
import { chronomapTheme } from './theme/flowbiteTheme';
import * as Sentry from '@sentry/react';

const App: React.FC = () => {
  // Respect system preference, but allow user override via localStorage
  useEffect(() => {
    const root = document.documentElement;
    const stored = localStorage.getItem('chronomap-theme');

    if (stored === 'dark' || stored === 'light') {
      root.classList.toggle('dark', stored === 'dark');
    } else if (window.matchMedia('(prefers-color-scheme: dark)').matches) {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = (e: MediaQueryListEvent) => {
      // Only follow system if user hasn't set an explicit preference
      if (!localStorage.getItem('chronomap-theme')) {
        root.classList.toggle('dark', e.matches);
      }
    };
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  return (
    <Sentry.ErrorBoundary fallback={<AppCrashFallback />}>
      <ThemeProvider theme={chronomapTheme} applyTheme="replace">
        <Authenticator.Provider>
          <SnackbarProvider
            maxSnack={3}
            autoHideDuration={3000}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          >
            <DataManagerProvider>
              <Router />
            </DataManagerProvider>
          </SnackbarProvider>
        </Authenticator.Provider>
      </ThemeProvider>
    </Sentry.ErrorBoundary>
  );
};

/** Last-resort fallback when the entire app crashes. Intentionally minimal —
 *  no providers, no theme, no router — just raw HTML so it can't itself fail. */
const AppCrashFallback: React.FC = () => (
  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', background: '#0d1117', color: '#c9d1d9', fontFamily: 'monospace', padding: '2rem', textAlign: 'center' }}>
    <div>
      <h1 style={{ fontSize: '1.5rem', marginBottom: '0.75rem' }}>Something went wrong</h1>
      <p style={{ marginBottom: '1.5rem', color: '#8b949e' }}>
        The error has been reported automatically. Try reloading the page.
      </p>
      <button
        onClick={() => window.location.reload()}
        style={{ padding: '0.5rem 1.5rem', background: '#f97316', color: '#fff', border: 'none', borderRadius: '0.375rem', cursor: 'pointer', fontSize: '0.875rem' }}
      >
        Reload
      </button>
    </div>
  </div>
);

export default App;
